import { Formik, Form, FormikProps } from 'formik';

import * as Yup from 'yup';
import cn from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import jwt_decode from 'jwt-decode';

import styles from './ResetPasswordForm.module.scss';
import { baseUrl, instance } from 'utils';
import { tryLogin } from 'api';
import { changeLanguage } from 'i18next';

interface IFormProps {
  email: string;
  password: string;
  terms: boolean;
}

interface IJwtPayload {
  email: string;
}

export const ResetPasswordForm = () => {
  const [passError, setPassError] = useState('');
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [docs, setDocs] = useState([]);
  const { t } = useTranslation();

  const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .min(2, `${t('password min')} 2`)
      .required(`${t('password required')}`),
    terms: Yup.boolean().oneOf([true], `${t('terms accept')}`)
  });

  const search = useLocation().search;
  const navigate = useNavigate();

  useEffect(() => {
    const takedToken = new URLSearchParams(search).get('token');
    const takedLang = new URLSearchParams(search).get('lang');
    const decodedToken = takedToken ? jwt_decode<IJwtPayload>(takedToken) : null;

    setToken(takedToken);
    changeLanguage(takedLang);

    setEmail(decodedToken?.email);

    instance.get('/items/legal_documents/').then(response => {
      const data = response.data.data;
      setDocs(data);
    });
  }, []);

  const handleFormSubmit = async (values: IFormProps) => {
    const data = {
      token: token,
      password: values.password
    };

    const loginData = {
      email: email,
      password: values.password
    };

    try {
      setIsDisabledButton(true);
      const response = await instance.post('/auth/password/reset', data);

      const status = response.status;
      if (status === 204) {
        const login = await tryLogin(loginData);
        const loginStatus = login.status;
        const tokens = JSON.stringify(login.data?.data);
        const language = localStorage.getItem('i18nextLng') || null;

        if (loginStatus === 200) {
          localStorage.setItem('authTokens', tokens);
          if (localStorage.getItem('authTokens')) {
            navigate('/dashboard');
          }
          if (language) {
            await instance.patch(`/users/me`, {
              language: language
            });
          }
        }
      }
    } catch (error) {
      const err = error as AxiosError;

      if (err.response) {
        const errorMsg = err.response.data.errors[0].message;
        setPassError(errorMsg);
      }
    } finally {
      setIsDisabledButton(false);
    }
  };

  const formattedLang = language => {
    const languageArr = language.split('-');
    return languageArr[0];
  };

  const getTermDoc = docs
    .filter(item => item.document_type == 'terms')
    .filter(item => item.language == formattedLang(localStorage.getItem('i18nextLng')));
  const getPrivacyDoc = docs
    .filter(item => item.document_type == 'privacy')
    .filter(item => item.language == formattedLang(localStorage.getItem('i18nextLng')));

  const initialValues: IFormProps = {
    email: '',
    password: '',
    terms: false
  };

  return (
    <div className={styles.root}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ResetPasswordSchema}
      >
        {({ values, errors, handleChange, touched }: FormikProps<IFormProps>) => (
          <Form className={styles.form}>
            <div className={styles.group}>
              {errors.email && touched.email && (
                <div className={styles.errorTxt}>{errors.email}</div>
              )}
              {passError && !errors.email && touched.email && (
                <div className={styles.errorTxt}>{passError}</div>
              )}
              <input
                type="email"
                value={email}
                onChange={handleChange('email')}
                readOnly
                disabled
                name="email"
                id="email"
                placeholder={t('email')}
                className={cn(
                  styles.input,
                  (errors.email && touched.email) || passError ? styles.error : ''
                )}
              />
            </div>
            <div className={styles.group}>
              {errors.password && touched.password && (
                <div className={styles.errorTxt}>{errors.password}</div>
              )}
              <input
                type="password"
                value={values.password}
                onChange={handleChange('password')}
                onInput={() => {
                  setPassError('');
                }}
                name="password"
                id="password"
                placeholder={t('password')}
                className={cn(
                  styles.input,
                  (errors.password && touched.password) || passError ? styles.error : ''
                )}
              />
            </div>
            <div className={styles.group}>
              {errors.terms && touched.terms && (
                <div className={styles.errorTxt}>{errors.terms}</div>
              )}
              <label className={styles.label}>
                <input
                  type="checkbox"
                  name="terms"
                  id="terms"
                  onChange={handleChange('terms')}
                  className={cn(
                    styles.checkbox,
                    (errors.terms && touched.terms) || passError ? styles.error : ''
                  )}
                />
                <div className={styles.labelTxt}>
                  {t('I agree to the')}{' '}
                  <a
                    href={`${baseUrl}/legal-docs/${getTermDoc[0]?.document_type}/${getTermDoc[0]?.language}`}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    {t('terms of use')}
                  </a>{' '}
                  {t('and')}{' '}
                  <a
                    href={`${baseUrl}/legal-docs/${getPrivacyDoc[0]?.document_type}/${getPrivacyDoc[0]?.language}`}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    {t('privacy policy')}
                  </a>{' '}
                  {t('of the KEEPI platform')}
                </div>
              </label>
            </div>
            <button
              type="submit"
              className={styles.btn}
              disabled={
                !!(errors.email && touched.email) ||
                !!(errors.password && touched.password) ||
                isDisabledButton
              }
            >
              {t('reset password')}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};
